/**
 * Lightweight per-tab browser fingerprint.
 *
 * Goal: give the backend a stable string that survives reloads in the
 * same tab but changes when the student opens the quiz in a different
 * browser/profile/device. Backend uses it to flag fingerprint drift
 * inside a single quiz attempt (Phase 1: log only).
 *
 * Strategy: a one-time hash of `navigator.userAgent + canvas signature
 * + screen size`, cached in `sessionStorage` so every request from the
 * same tab returns the same value without recomputing the canvas hash.
 *
 * NOT a fraud-grade fingerprint. We accept that adversaries can spoof
 * it; the value is in catching honest-to-goodness device changes
 * mid-attempt (e.g. someone hands the laptop off).
 */

const SESSION_KEY = 'elearning_client_fingerprint';

function canvasSignature() {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'no-2d';
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(0, 0, 200, 50);
    ctx.fillStyle = '#069';
    ctx.fillText('uniapp-fingerprint', 2, 2);
    return canvas.toDataURL().slice(-64);
  } catch (_) {
    return 'canvas-error';
  }
}

function djb2(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i);
  // Force unsigned 32-bit then base36 — short, opaque, URL-safe.
  return (h >>> 0).toString(36);
}

export function getClientFingerprint() {
  try {
    const cached = sessionStorage.getItem(SESSION_KEY);
    if (cached) return cached;
  } catch (_) {
    // Private mode / disabled storage — recompute every call. That breaks
    // stability but the BE classifies each save independently anyway.
  }

  const parts = [
    navigator.userAgent || '',
    navigator.language || '',
    `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio || 1}`,
    new Date().getTimezoneOffset(),
    canvasSignature(),
  ].join('|');

  const fp = djb2(parts);

  try { sessionStorage.setItem(SESSION_KEY, fp); } catch (_) { /* noop */ }
  return fp;
}
